import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import type {
  EmailShareRecipientContract,
  EmailShareTemplateCategory,
  EmailShareTemplateContract,
} from '@/contracts/share'
import { Save } from 'lucide-react'
import { useEffect, useState } from 'react'

const categoryLabels: Record<EmailShareTemplateCategory, string> = {
  general_invitation: '一般問卷邀請',
  b2b_customer_onboarding: 'B2B 新客建檔邀請',
  event_feedback: '活動回饋問卷',
  sales_followup: '業務跟進問卷',
}

type EmailTemplateEditorDialogProps = {
  open: boolean
  template: EmailShareTemplateContract | null
  salesRecipients: EmailShareRecipientContract[]
  onOpenChange: (open: boolean) => void
  onSave: (template: EmailShareTemplateContract) => void
}

export function EmailTemplateEditorDialog({
  open,
  template,
  salesRecipients,
  onOpenChange,
  onSave,
}: EmailTemplateEditorDialogProps) {
  const [draft, setDraft] = useState<EmailShareTemplateContract | null>(template)

  useEffect(() => {
    setDraft(template)
  }, [template])

  if (!draft) return null

  function toggleSalesUser(salesUserId: string | undefined) {
    if (!draft || !salesUserId) return
    const exists = draft.defaultCcSalesUserIds.includes(salesUserId)
    setDraft({
      ...draft,
      defaultCcSalesUserIds: exists
        ? draft.defaultCcSalesUserIds.filter((id) => id !== salesUserId)
        : [...draft.defaultCcSalesUserIds, salesUserId],
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[calc(100vh-48px)] max-w-3xl overflow-hidden p-0">
        <DialogHeader className="border-b border-slate-200 px-5 py-4">
          <DialogTitle>編輯問卷寄送範本</DialogTitle>
          <DialogDescription>
            修改範本名稱、信件標題與內容。這裡只儲存 mock-safe template contract。
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[calc(100vh-180px)]" viewportClassName="p-5">
          <div className="space-y-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>範本名稱</Label>
                <Input
                  value={draft.name}
                  onChange={(event) => setDraft({ ...draft, name: event.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>範本類型</Label>
                <Select
                  value={draft.category}
                  onValueChange={(value) =>
                    setDraft({ ...draft, category: value as EmailShareTemplateCategory })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(categoryLabels).map(([value, label]) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>範本說明</Label>
              <Input
                value={draft.description}
                onChange={(event) => setDraft({ ...draft, description: event.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>信件標題</Label>
              <Input
                value={draft.subject}
                onChange={(event) => setDraft({ ...draft, subject: event.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>信件內容</Label>
              <Textarea
                value={draft.body}
                rows={10}
                onChange={(event) => setDraft({ ...draft, body: event.target.value })}
              />
            </div>

            <div className="rounded-lg border border-slate-200 bg-white p-4">
              <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <Label>預設 CC 策略</Label>
                  <p className="mt-1 text-sm text-slate-500">
                    套用範本時自動帶入的業務副本；仍可在寄送畫面手動增減。
                  </p>
                </div>
                <Badge variant="outline">mock-safe</Badge>
              </div>
              <div className="mt-4 grid gap-4 lg:grid-cols-[260px_minmax(0,1fr)]">
                <Select
                  value={draft.defaultCcStrategy}
                  onValueChange={(value) =>
                    setDraft({
                      ...draft,
                      defaultCcStrategy: value as EmailShareTemplateContract['defaultCcStrategy'],
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">不自動 CC</SelectItem>
                    <SelectItem value="selected_sales_users">指定業務名單</SelectItem>
                    <SelectItem value="form_sales_owner">問卷負責業務</SelectItem>
                  </SelectContent>
                </Select>

                <div className="rounded-md border border-slate-200 bg-slate-50">
                  <div className="border-b border-slate-200 px-3 py-2 text-xs font-semibold text-slate-500">
                    預設 CC 業務
                  </div>
                  <ScrollArea className="h-44" viewportClassName="p-2">
                    <div className="space-y-1">
                      {salesRecipients.map((recipient) => {
                        const checked = Boolean(
                          recipient.salesUserId &&
                            draft.defaultCcSalesUserIds.includes(recipient.salesUserId),
                        )
                        return (
                          <label
                            key={recipient.id}
                            className="flex cursor-pointer items-start gap-2 rounded-md px-2 py-2 text-sm transition hover:bg-white"
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleSalesUser(recipient.salesUserId)}
                              className="mt-0.5 size-4 rounded border-slate-300 accent-indigo-600"
                            />
                            <span className="min-w-0 flex-1">
                              <span className="block truncate font-medium text-slate-800">
                                {recipient.name}
                              </span>
                              <span className="block truncate text-xs text-slate-500">
                                {recipient.email}
                              </span>
                            </span>
                          </label>
                        )
                      })}
                    </div>
                  </ScrollArea>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <div className="text-sm font-semibold text-slate-950">可用變數</div>
              <div className="mt-3 flex flex-wrap gap-2">
                {draft.variables.map((variable) => (
                  <Badge key={variable} variant="outline" className="bg-white font-mono">
                    {variable}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        </ScrollArea>

        <div className="flex justify-end gap-2 border-t border-slate-200 px-5 py-4">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            取消
          </Button>
          <Button
            type="button"
            onClick={() => {
              onSave(draft)
              onOpenChange(false)
            }}
          >
            <Save className="size-4" />
            儲存範本
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
